import { IFrontVariation } from 'models/product'

/**
 * Генерирует название для товара-вариации на основе характеристик
 * @param variation - вариация товара
 * @param parentName - название основного товара
 * @returns сгенерированное название товара-вариации
 */
export const generateVariationProductName = (
  variation: IFrontVariation,
  parentName: string = ''
): string => {
  const getVariationSuffix = (): string => {
    if (
      variation.firstCharacteristicValue &&
      variation.secondCharacteristicValue
    ) {
      return `(${variation.firstCharacteristicValue.toLowerCase()}, ${variation.secondCharacteristicValue.toLowerCase()})`
    }
    if (variation.firstCharacteristicValue) {
      return `(${variation.firstCharacteristicValue.toLowerCase()})`
    }
    if (variation.secondCharacteristicValue) {
      return `(${variation.secondCharacteristicValue.toLowerCase()})`
    }
    return ''
  }

  const suffix = getVariationSuffix()
  return `${parentName} ${suffix}`.trim()
}

/**
 * Обновляет названия товаров-вариаций в массиве вариаций
 * @param variations - массив вариаций
 * @param parentName - название основного товара
 * @returns обновленный массив вариаций с новыми названиями товаров
 */
export const updateVariationProductNames = (
  variations: IFrontVariation[],
  parentName: string = ''
): IFrontVariation[] => {
  return variations.map((variation) => {
    if (variation.product) {
      // Проверяем, является ли название товара автоматически сгенерированным
      const isGenerated = isGeneratedVariationName(variation.product.name, parentName)

      if (isGenerated) {
        // Только для автоматически сгенерированных названий обновляем по шаблону
        const newName = generateVariationProductName(variation, parentName)

        // Обновляем название только если оно изменилось
        if (variation.product.name !== newName) {
          return {
            ...variation,
            product: {
              ...variation.product,
              name: newName
            }
          }
        }
      } else {
        // Для оригинальных названий товаров обновляем только характеристики в скобках
        const originalName = extractOriginalProductName(variation.product.name)
        const newName = createVariationNameWithOriginal(originalName, variation)

        // Обновляем название только если оно изменилось
        if (variation.product.name !== newName) {
          return {
            ...variation,
            product: {
              ...variation.product,
              name: newName
            }
          }
        }
      }
    }
    return variation
  })
}

/**
 * Проверяет, есть ли изменения в названиях товаров-вариаций
 * @param originalVariations - исходный массив вариаций
 * @param updatedVariations - обновленный массив вариаций
 * @returns true если есть изменения в названиях
 */
export const hasVariationNameChanges = (
  originalVariations: IFrontVariation[],
  updatedVariations: IFrontVariation[]
): boolean => {
  return updatedVariations.some(
    (variation, index) =>
      variation.product?.name !== originalVariations[index]?.product?.name
  )
}

/**
 * Извлекает оригинальное название товара из названия с характеристиками
 * @param fullName - полное название товара с характеристиками
 * @returns оригинальное название товара без характеристик
 */
export const extractOriginalProductName = (fullName: string): string => {
  // Удаляем ОДНУ ИЛИ НЕСКОЛЬКО завершающих групп в скобках из конца строки,
  // например: "Название (36)" или "Название (36) (кожа) (черный)"
  // Оставляем только исходное "Название"
  const withoutTrailingGroups = fullName.replace(/\s*(\([^)]*\)\s*)+$/, '').trim()
  return withoutTrailingGroups
}

/**
 * Создает название товара-вариации с сохранением оригинального названия
 * @param originalName - оригинальное название товара
 * @param variation - вариация товара
 * @returns название товара с добавленными характеристиками в скобках
 */
export const createVariationNameWithOriginal = (
  originalName: string,
  variation: IFrontVariation
): string => {
  const getVariationSuffix = (): string => {
    if (
      variation.firstCharacteristicValue &&
      variation.secondCharacteristicValue
    ) {
      return `(${variation.firstCharacteristicValue.toLowerCase()}, ${variation.secondCharacteristicValue.toLowerCase()})`
    }
    if (variation.firstCharacteristicValue) {
      return `(${variation.firstCharacteristicValue.toLowerCase()})`
    }
    if (variation.secondCharacteristicValue) {
      return `(${variation.secondCharacteristicValue.toLowerCase()})`
    }
    return ''
  }

  const suffix = getVariationSuffix()
  return suffix ? `${originalName} ${suffix}` : originalName
}

/**
 * Проверяет, является ли название товара сгенерированным (содержит характеристики в скобках)
 * @param productName - название товара
 * @param parentName - название основного товара
 * @returns true если название сгенерировано автоматически
 */
export const isGeneratedVariationName = (
  productName: string,
  parentName: string
): boolean => {
  // Проверяем, начинается ли название товара с названия основного товара
  // и содержит ли характеристики в скобках в конце
  const trimmedProductName = productName.trim()
  const trimmedParentName = parentName.trim()

  if (!trimmedProductName.startsWith(trimmedParentName)) {
    return false
  }

  const remainingPart = trimmedProductName.substring(trimmedParentName.length).trim()

  // Проверяем, что оставшаяся часть - это ОДНА ИЛИ НЕСКОЛЬКО групп характеристик в скобках
  // Пример допустимого: "(36)", "(36) (кожа)", "(36, черный)"
  if (!/^(\([^)]+\)\s*)+$/.test(remainingPart)) {
    return false
  }

  // Дополнительная проверка: характеристики должны содержать только буквы, цифры, пробелы и разделители
  const characteristicsMatch = remainingPart.match(/\(([^)]+)\)/)
  if (!characteristicsMatch) {
    return false
  }

  const characteristics = characteristicsMatch[1]
  // Проверяем, что характеристики содержат только допустимые символы
  return /^[a-zA-Zа-яА-ЯёЁ0-9\s\-/,.]+$/.test(characteristics)
}